import { useQuery } from '@tanstack/react-query'
import { useDebounce } from '@uidotdev/usehooks'
import React, { useState } from 'react'
import { FormatOptionLabelMeta } from 'react-select'
import invariant from 'tiny-invariant'

import ReactSelectDropdownIndicator from '@/components/misc/ReactSelectDropdownIndicator'
import Select from '@/components/select/Select'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  buildMultiSelectStyles,
  buildSingleSelectStyles,
  SelectStyles,
} from '@/lib/helpers/reactSelectHelpers'
import { QUERY_KEYS } from '@/lib/queryKeys'
import companyService from '@/lib/services/companyService'
import referralFlowService, {
  BLOCKED_BY_ROUTING_RULE_BADGE_LABEL,
} from '@/lib/services/referralFlowService'
import { cn } from '@/lib/utils'
import { ReferralIndividual, SelectOption } from '@/types/misc'
import { GetReferralFirmIndividualsParams } from '@/types/requests/companies'
import { ReferralFirmIndividualsCollectionItem } from '@/types/responses/companies'

interface Props {
  key?: string
  company: {
    id: number
    presentationName: string
    isLimitedToSingleAdviser?: boolean | null
  }
  selected: ReferralIndividual[]
  onSelect: (selected: ReferralIndividual[]) => void
  additionalParams?: Omit<GetReferralFirmIndividualsParams, 'fullName'>
  /**
   * When set, only these users are selectable (e.g. a SENDER_SELECTS routing
   * rule's eligible adviser pool). Everyone else still appears but is
   * disabled with a "Not available" pill.
   */
  eligibleUserIds?: number[]
  isDisabled?: boolean
  noOptionsMessage?: React.ComponentProps<typeof Select>['noOptionsMessage']
  userIdsToExclude?: number[]
}

const SelectCompanyIndividuals: React.FC<Props> = ({
  company,
  selected,
  onSelect,
  additionalParams,
  eligibleUserIds,
  noOptionsMessage,
  userIdsToExclude = [],
  ...props
}) => {
  const [individualName, setIndividualName] = useState('')
  const debouncedIndividualName = useDebounce(individualName, 400)

  const params: GetReferralFirmIndividualsParams = {
    ...additionalParams,
  }

  if (debouncedIndividualName) {
    params.fullName = debouncedIndividualName
  }

  const referralFirmIndividualsQuery = useQuery({
    queryKey: QUERY_KEYS.referralFirmIndividuals({
      ...params,
      referralFirmId: company.id,
    }),
    queryFn: () =>
      companyService.getReferralFirmIndividuals(company.id, params),
    staleTime: 20000,
  })

  const referralFirmIndividualResults =
    referralFirmIndividualsQuery.data?.['hydra:member'] || []

  const isLoading = referralFirmIndividualsQuery.isLoading
  const isMulti = !company.isLimitedToSingleAdviser

  const selectedOptions = selected
    ? selected.map((individual) => ({
        label: individual.fullName,
        value: individual.companyUserId.toString(),
      }))
    : null

  function handleChange(options: SelectOption[]) {
    const selectedOptionsArray = Array.isArray(options)
      ? options
      : options
        ? [options]
        : []

    const individuals = selectedOptionsArray.map(
      (option): ReferralIndividual => {
        const individual = referralFirmIndividualResults.find(
          (individual) => individual.id === Number(option.value),
        )

        invariant(individual, `Individual with id ${option.value} not found`)

        return {
          userId: individual.user.id,
          companyUserId: individual.id,
          fullName: individual.user.fullName,
          company: individual.company,
        }
      },
    )

    onSelect(individuals)
  }

  const styles = isMulti
    ? buildMultiSelectStyles({})
    : buildSingleSelectStyles({})

  const options: SelectOption[] = referralFirmIndividualResults
    .filter((individual) => !userIdsToExclude.includes(individual.user.id))
    .map((referralFirmIndividual) =>
      makeOption(referralFirmIndividual, eligibleUserIds),
    )

  return (
    <Select
      options={options}
      inputValue={individualName}
      className="bg-white"
      onInputChange={setIndividualName}
      isLoading={isLoading}
      styles={styles as SelectStyles}
      isClearable
      isMulti={isMulti}
      formatOptionLabel={(option, meta) =>
        formatOptionLabel({
          option,
          meta,
          isMulti,
          individuals: referralFirmIndividualResults,
          eligibleUserIds,
          companyName: company.presentationName,
        })
      }
      controlShouldRenderValue
      closeMenuOnSelect={!isMulti}
      hideSelectedOptions={false}
      components={{ DropdownIndicator: ReactSelectDropdownIndicator }}
      value={selectedOptions}
      onChange={(options) => handleChange(options as SelectOption[])}
      placeholder={
        isLoading
          ? 'Loading...'
          : isMulti
            ? 'Search individuals...'
            : 'Search individual...'
      }
      noOptionsMessage={
        noOptionsMessage
          ? noOptionsMessage
          : ({ inputValue }) => {
              return inputValue
                ? `No individuals found with the name '${inputValue}'`
                : 'No individuals found'
            }
      }
      {...props}
    />
  )
}

function makeOption(
  referralFirmIndividual: ReferralFirmIndividualsCollectionItem,
  eligibleUserIds?: number[],
): SelectOption {
  return {
    label: referralFirmIndividual.user.fullName,
    value: referralFirmIndividual.id.toString(),
    isDisabled: isBlockedByRoutingRule(referralFirmIndividual, eligibleUserIds),
  }
}

function isBlockedByRoutingRule(
  referralFirmIndividual: ReferralFirmIndividualsCollectionItem,
  eligibleUserIds?: number[],
): boolean {
  return (
    !!eligibleUserIds &&
    !eligibleUserIds.includes(referralFirmIndividual.user.id)
  )
}

function formatOptionLabel(options: {
  option: SelectOption
  meta: FormatOptionLabelMeta<SelectOption>
  isMulti: boolean
  individuals: ReferralFirmIndividualsCollectionItem[]
  eligibleUserIds?: number[]
  companyName: string
}) {
  const { option, meta, isMulti, individuals, eligibleUserIds, companyName } =
    options
  const { label, value } = option
  const { context, selectValue } = meta

  if (context !== 'menu') {
    return label
  }

  const isSelected = selectValue
    .map((selectedValue) => selectedValue.value)
    .includes(value)

  const referralFirmIndividual = individuals.find(
    (individual) => individual.id.toString() === value,
  )
  const isBlocked =
    !!referralFirmIndividual &&
    isBlockedByRoutingRule(referralFirmIndividual, eligibleUserIds)

  return (
    <div
      className={cn('flex flex-1 items-start', {
        'text-disabled': isBlocked,
      })}
    >
      {isMulti && (
        <Checkbox
          checked={isSelected}
          className="mt-0.75"
          style={{ minWidth: '16px', minHeight: '16px', marginRight: '9px' }}
        />
      )}

      <div>
        {label}
        {isBlocked && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="secondary"
                size="sm"
                className="relative ml-1"
                style={{ top: '-2px' }}
              >
                {BLOCKED_BY_ROUTING_RULE_BADGE_LABEL}
              </Badge>
            </TooltipTrigger>

            <TooltipContent>
              {referralFlowService.getBlockedByRoutingRuleMessage(companyName)}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  )
}

export default SelectCompanyIndividuals
